import html2canvas from 'html2canvas';

function parseTranslateFromCssTransform(transform: string): { x: number; y: number } {
  const t = (transform || '').trim();
  if (!t || t === 'none') return { x: 0, y: 0 };

  // matrix(a, b, c, d, tx, ty)
  const m2 = t.match(/^matrix\(([^)]+)\)$/);
  if (m2) {
    const parts = m2[1]
      .split(',')
      .map((p) => parseFloat(p.trim()))
      .filter((n) => Number.isFinite(n));
    if (parts.length === 6) {
      return { x: parts[4] || 0, y: parts[5] || 0 };
    }
  }

  // matrix3d(..., tx, ty, tz) where tx=13th, ty=14th (0-based 12,13)
  const m3 = t.match(/^matrix3d\(([^)]+)\)$/);
  if (m3) {
    const parts = m3[1]
      .split(',')
      .map((p) => parseFloat(p.trim()))
      .filter((n) => Number.isFinite(n));
    if (parts.length === 16) {
      return { x: parts[12] || 0, y: parts[13] || 0 };
    }
  }

  return { x: 0, y: 0 };
}

function parseClipRect(clip: string): { top: number; right: number; bottom: number; left: number } | null {
  const raw = (clip || '').trim();
  if (!raw || raw === 'auto' || raw === 'none') return null;

  const m = raw.match(/^rect\((.*)\)$/i);
  if (!m) return null;

  const parts = m[1]
    .replace(/,/g, ' ')
    .split(/\s+/)
    .map((p) => p.trim())
    .filter((p) => !!p);

  if (parts.length !== 4) return null;
  if (parts.some((p) => p.toLowerCase() === 'auto')) return null;

  const nums = parts.map((p) => parseFloat(p));
  if (nums.some((n) => !Number.isFinite(n))) return null;

  return { top: nums[0], right: nums[1], bottom: nums[2], left: nums[3] };
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export const COLLECTION_REPORT_CAPTURE_MAX_DIM = 1200;

function resolveCollectionReportCaptureScale(
  el: HTMLElement,
  maxDim = COLLECTION_REPORT_CAPTURE_MAX_DIM,
): number {
  const cssWidth = Math.max(1, el.getBoundingClientRect().width || el.offsetWidth || el.clientWidth || 0);
  const cssHeight = Math.max(1, el.getBoundingClientRect().height || el.offsetHeight || el.clientHeight || 0);
  const maxCssDim = Math.max(cssWidth, cssHeight);
  const deviceScale =
    typeof window !== 'undefined' && typeof window.devicePixelRatio === 'number'
      ? Math.min(2, Math.max(1, window.devicePixelRatio))
      : 1;

  return Math.max(0.1, Math.min(deviceScale, maxDim / maxCssDim));
}

function releaseCanvas(canvas: HTMLCanvasElement | null | undefined): void {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
}

function resolveLeafletCompareDividerVisualX(el: HTMLElement, fallbackX: number): number {
  const divider = el.querySelector<HTMLElement>('.leaflet-sbs-divider');
  if (!divider) return fallbackX;

  const rootRect = el.getBoundingClientRect();
  const dividerRect = divider.getBoundingClientRect();
  if (!rootRect.width || !dividerRect.width) return fallbackX;

  return dividerRect.right - rootRect.left;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality = 0.85): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to encode screenshot.'));
        },
        'image/jpeg',
        quality,
      );
      return;
    }

    // Fallback for older browsers: dataURL -> Blob
    try {
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      const base64 = dataUrl.split(',')[1] || '';
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      resolve(new Blob([bytes], { type: 'image/jpeg' }));
    } catch (e) {
      reject(e);
    }
  });
}

function resolveCaptureScope(clonedDoc: Document, el: HTMLElement): HTMLElement {
  const rootId = el.id;
  const root = rootId ? clonedDoc.getElementById(rootId) : null;
  return (root as HTMLElement | null) || clonedDoc.body;
}

function applyLeafletTransformNormalize(scope: HTMLElement, view: Window): void {
  const nodes = scope.querySelectorAll<HTMLElement>(
    '.leaflet-zoom-animated, .leaflet-tile-container, .leaflet-pane canvas',
  );

  nodes.forEach((node) => {
    const cs = view.getComputedStyle(node);
    if (!cs) return;
    if (cs.position !== 'absolute') return;

    const { x, y } = parseTranslateFromCssTransform(cs.transform);
    if (!x && !y) return;

    const left = cs.left && cs.left !== 'auto' ? parseFloat(cs.left) : 0;
    const top = cs.top && cs.top !== 'auto' ? parseFloat(cs.top) : 0;

    node.style.transform = 'none';
    node.style.left = `${(Number.isFinite(left) ? left : 0) + x}px`;
    node.style.top = `${(Number.isFinite(top) ? top : 0) + y}px`;
  });
}

function applyLeafletClipToClipPath(scope: HTMLElement, view: Window): void {
  // Leaflet compare/split uses legacy `clip: rect(...)` on panes.
  // html2canvas often ignores `clip` but may respect `clip-path`, so translate it in the clone.
  const panes = scope.querySelectorAll<HTMLElement>('.leaflet-pane');
  panes.forEach((pane) => {
    const cs = view.getComputedStyle(pane);
    if (!cs) return;

    const rect = parseClipRect(cs.clip);
    if (!rect) return;

    const width = pane.offsetWidth;
    const height = pane.offsetHeight;
    if (!width || !height) return;

    // Leaflet's clip rect can include negative coords depending on map transform;
    // clip-path inset() behaves best with values clamped to the element box.
    const insetTop = clamp(rect.top, 0, height);
    const insetLeft = clamp(rect.left, 0, width);
    const insetRight = clamp(width - rect.right, 0, width);
    const insetBottom = clamp(height - rect.bottom, 0, height);

    pane.style.clipPath = `inset(${insetTop}px ${insetRight}px ${insetBottom}px ${insetLeft}px)`;
    (pane.style as any).webkitClipPath = pane.style.clipPath;
  });
}

function applyAoiCloneOverrides(scope: HTMLElement, options: CaptureElementAsJpegOptions): void {
  if (options.aoiOnlyInClone) {
    // Hide Leaflet controls in AOI-only export.
    const controls = scope.querySelectorAll<HTMLElement>('.leaflet-control-container, .leaflet-control');
    controls.forEach((c) => {
      c.style.display = 'none';
      c.style.visibility = 'hidden';
      c.style.opacity = '0';
    });

    // Hide markers/popups/tooltips (but keep any element explicitly marked as part of the AOI/target capture).
    const hidePanes = [
      '.leaflet-marker-pane',
      '.leaflet-shadow-pane',
      '.leaflet-popup-pane',
      '.leaflet-tooltip-pane',
    ];
    hidePanes.forEach((sel) => {
      const pane = scope.querySelector(sel);
      if (!pane) return;
      pane.querySelectorAll<HTMLElement>('*').forEach((node) => {
        const isAoi =
          typeof (node as any).closest === 'function'
            ? !!(node as any).closest('.aoi-capture-layer')
            : false;
        if (isAoi) {
          node.style.opacity = '1';
          node.style.visibility = 'visible';
          return;
        }
        node.style.opacity = '0';
        node.style.visibility = 'hidden';
      });
    });

    // Hide all overlay vectors EXCEPT the AOI layer.
    const overlayPane = scope.querySelector('.leaflet-overlay-pane');
    if (overlayPane) {
      overlayPane.querySelectorAll<HTMLElement>('*').forEach((node) => {
        const isAoi = typeof (node as any).closest === 'function' ? !!(node as any).closest('.aoi-capture-layer') : false;
        if (isAoi) return;

        node.style.opacity = '0';
        node.style.visibility = 'hidden';

        const anyNode = node as any;
        if (typeof anyNode.setAttribute === 'function') {
          try {
            anyNode.setAttribute('stroke-opacity', '0');
            anyNode.setAttribute('fill-opacity', '0');
          } catch {
            // Ignore
          }
        }
      });
    }
  }

  if (options.revealAoiInClone || options.aoiOnlyInClone) {
    const aoiElements = scope.querySelectorAll<HTMLElement>('.aoi-capture-layer, .aoi-capture-layer *');
    aoiElements.forEach((node) => {
      node.style.opacity = '1';
      node.style.visibility = 'visible';

      // Leaflet SVG renderer uses attributes for opacity; override them for the clone.
      const anyNode = node as any;
      if (typeof anyNode.setAttribute === 'function') {
        try {
          anyNode.setAttribute('stroke-opacity', '1');
          anyNode.setAttribute('fill-opacity', '0.12');
        } catch {
          // Ignore
        }
      }
    });
  }
}

type OnCloneExtra = (args: { clonedDoc: Document; scope: HTMLElement; view: Window }) => void;

function delay(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

async function waitForImageLoad(img: HTMLImageElement): Promise<void> {
  if (img.complete) {
    if (img.naturalWidth > 0 && typeof img.decode === 'function') {
      try {
        await img.decode();
      } catch {
        // Ignore decode failures and let capture continue with the loaded pixels.
      }
    }
    return;
  }

  await new Promise<void>((resolve) => {
    const cleanup = () => {
      img.removeEventListener('load', onDone);
      img.removeEventListener('error', onDone);
    };
    const onDone = () => {
      cleanup();
      resolve();
    };

    img.addEventListener('load', onDone, { once: true });
    img.addEventListener('error', onDone, { once: true });
  });

  if (img.naturalWidth > 0 && typeof img.decode === 'function') {
    try {
      await img.decode();
    } catch {
      // Ignore decode failures and let capture continue with the loaded pixels.
    }
  }
}

async function waitForElementImagesToSettle(el: HTMLElement, timeoutMs = 4000): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  let stablePasses = 0;

  while (Date.now() < deadline) {
    const images = Array.from(el.querySelectorAll('img')).filter((img) => {
      const src = img.currentSrc || img.getAttribute('src') || '';
      return src.trim().length > 0;
    });

    const pendingImages = images.filter((img) => !img.complete);
    if (!pendingImages.length) {
      stablePasses += 1;
      if (stablePasses >= 2) {
        return;
      }

      await delay(80);
      continue;
    }

    stablePasses = 0;
    await Promise.race([
      Promise.all(pendingImages.map((img) => waitForImageLoad(img))),
      delay(250),
    ]);
  }
}

async function captureElementAsCanvas(
  el: HTMLElement,
  options: CaptureElementAsJpegOptions,
  extra?: OnCloneExtra,
): Promise<HTMLCanvasElement> {
  const scrollX = typeof window !== 'undefined' ? window.scrollX || 0 : 0;
  const scrollY = typeof window !== 'undefined' ? window.scrollY || 0 : 0;

  const captureScale = resolveCollectionReportCaptureScale(
    el,
    typeof options.maxDim === 'number' && options.maxDim > 0
      ? options.maxDim
      : COLLECTION_REPORT_CAPTURE_MAX_DIM,
  );

  await waitForElementImagesToSettle(el, options.imageSettleTimeoutMs ?? 4000);

  return await html2canvas(el, {
    useCORS: true,
    allowTaint: false,
    backgroundColor: '#ffffff',
    scale: captureScale,
    scrollX: -scrollX,
    scrollY: -scrollY,
    onclone: (clonedDoc) => {
      const scope = resolveCaptureScope(clonedDoc, el);
      const view = clonedDoc.defaultView;
      if (!view) return;

      applyLeafletTransformNormalize(scope, view);
      applyLeafletClipToClipPath(scope, view);
      applyAoiCloneOverrides(scope, options);

      extra?.({ clonedDoc, scope, view });
    },
    logging: false,
  });
}

async function canvasToCappedJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  const maxDim = COLLECTION_REPORT_CAPTURE_MAX_DIM;
  const width = canvas.width;
  const height = canvas.height;
  const scale = Math.min(1, maxDim / Math.max(width, height));

  if (scale >= 1) {
    return await canvasToJpegBlob(canvas, 0.85);
  }

  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(width * scale));
  out.height = Math.max(1, Math.round(height * scale));

  const ctx = out.getContext('2d');
  if (!ctx) {
    releaseCanvas(out);
    return await canvasToJpegBlob(canvas, 0.85);
  }

  try {
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return await canvasToJpegBlob(out, 0.85);
  } finally {
    releaseCanvas(out);
  }
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image.'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(blob);
  });
}

export type CollectionReportFeedTreeV1 = {
  v: 1;
  t?: 1;
  s: Array<{
    n: string;
    p?: string[];
    sc: Array<{
      n: string;
      f: string[];
      af?: string[];
    }>;
  }>;
};

export type CollectionReportSelectionsRenderRowV1 = {
  label: string;
  value?: string;
  children?: CollectionReportSelectionsRenderRowV1[];
};

export type CollectionReportSelectionsRenderSectionV1 = {
  title?: string;
  rows: CollectionReportSelectionsRenderRowV1[];
};

export type CollectionReportSelectionsSummaryV2 = {
  v: 2;
  sections?: CollectionReportSelectionsRenderSectionV1[];
};

function safeTrim(s: unknown): string {
  return typeof s === 'string' ? s.trim() : '';
}

function sanitizeCollectionReportSelectionRows(rows: unknown): CollectionReportSelectionsRenderRowV1[] {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row: any) => {
      const label = safeTrim(row?.label);
      const value = safeTrim(row?.value);
      const children = sanitizeCollectionReportSelectionRows(row?.children);

      if (!label && !value && !children.length) return null;

      const nextRow: CollectionReportSelectionsRenderRowV1 = {
        label: label || value || 'Entry',
      };

      if (value) nextRow.value = value;
      if (children.length) nextRow.children = children;

      return nextRow;
    })
    .filter(Boolean) as CollectionReportSelectionsRenderRowV1[];
}

function sanitizeCollectionReportSelectionSections(sections: unknown): CollectionReportSelectionsRenderSectionV1[] {
  if (!Array.isArray(sections)) return [];

  return sections
    .map((section: any) => {
      const title = safeTrim(section?.title);
      const rows = sanitizeCollectionReportSelectionRows(section?.rows);

      if (!title && !rows.length) return null;

      const nextSection: CollectionReportSelectionsRenderSectionV1 = {
        rows,
      };

      if (title) nextSection.title = title;

      return nextSection;
    })
    .filter(Boolean) as CollectionReportSelectionsRenderSectionV1[];
}

function safeJsonLength(x: unknown): number {
  try {
    return JSON.stringify(x).length;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

function toValidDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = new Date(trimmed);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }
  return null;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function formatCollectionReportDate(value: unknown): string {
  if (value == null || value === '') return '-';
  const parsed = toValidDate(value);
  if (!parsed) {
    return String(value).trim() || '-';
  }

  return `${pad2(parsed.getUTCDate())}/${pad2(parsed.getUTCMonth() + 1)}/${parsed.getUTCFullYear()}`;
}

export function formatCollectionReportDateRange(range: { start?: unknown; end?: unknown } | null | undefined): string {
  if (!range) return '-';
  return `${formatCollectionReportDate(range.start)} → ${formatCollectionReportDate(range.end)}`;
}

export function normalizeCollectionReportSceneLabel(label: unknown): string | null {
  if (label == null) return null;
  const text = String(label).trim();
  if (!text) return null;
  if (text.toLowerCase() === 'feed') return null;
  return text;
}

export function formatCollectionReportCompareItemLabel(item: any): string {
  if (!item) return '-';
  if (item?.subDailyCount != null && String(item.subDailyCount).trim()) {
    return `Sub-daily ${item.subDailyCount}: ${formatCollectionReportDateRange({
      start: item?.start ?? null,
      end: item?.end ?? null,
    })}`;
  }

  const date = safeTrim(item?.date);
  if (date) return date;

  if (
    (item?.start != null && String(item.start).trim()) ||
    (item?.end != null && String(item.end).trim())
  ) {
    return formatCollectionReportDateRange({ start: item?.start ?? null, end: item?.end ?? null });
  }

  const name = safeTrim(item?.name);
  if (name) return name;

  return 'Selection';
}

export function formatCollectionReportRefreshHistoryLabel(item: { dateTime?: unknown; refreshId?: unknown } | null | undefined): string {
  const parsed = toValidDate(item?.dateTime ?? null);
  if (parsed) {
    let hours = parsed.getUTCHours();
    const minutes = pad2(parsed.getUTCMinutes());
    const meridiem = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${pad2(parsed.getUTCDate())}/${pad2(parsed.getUTCMonth() + 1)}/${parsed.getUTCFullYear()} - ${pad2(hours)}.${minutes} ${meridiem} GMT`;
  }

  const rawDateTime = safeTrim(item?.dateTime);
  if (rawDateTime) return rawDateTime;

  const refreshId = safeTrim(item?.refreshId);
  return refreshId || '-';
}

export function resolveCollectionReportCurrentSceneLabel(args: {
  currentSceneLabel?: unknown;
  historySceneLabel?: unknown;
  checkedScenes?: unknown[];
}): string {
  const direct = normalizeCollectionReportSceneLabel(args.currentSceneLabel);
  if (direct) return direct;

  const history = normalizeCollectionReportSceneLabel(args.historySceneLabel);
  if (history) return history;

  const firstChecked = Array.isArray(args.checkedScenes)
    ? args.checkedScenes.map((item) => normalizeCollectionReportSceneLabel(item)).find(Boolean)
    : null;
  return firstChecked || '-';
}

export function getCollectionReportSensorDisplayName(args: {
  sensor: any;
  currentUser: any;
  isFeedTasking: boolean;
}): string {
  if (args.isFeedTasking) return safeTrim(args.sensor?.questionName) || safeTrim(args.sensor?.name);
  const base =
    args.currentUser?.obfuscated && safeTrim(args.sensor?.obfuscateName)
      ? safeTrim(args.sensor?.obfuscateName)
      : safeTrim(args.sensor?.name);
  const tnq = safeTrim(args.sensor?.tnqSensorType);
  return tnq ? `${base} (${tnq})` : base;
}

function getCollectionReportSceneSelectionPrefix(scene: any): string {
  const operatorKey = safeTrim(scene?.operatorKey).toLowerCase();

  if (operatorKey === 'usl' || operatorKey === 'spire-rf') return 'Collection';
  if (operatorKey === 'axelspace' || operatorKey === 'azista') return 'Tile';
  return 'Scene';
}

function getCollectionReportFeedDisplayName(feed: any): string {
  return safeTrim(feed?.feedName) || safeTrim(feed?.name);
}

function collectionReportFeedHasInsightForScene(feed: any, sceneId: any): boolean {
  const id = sceneId != null ? String(sceneId) : '';
  if (!id) return false;
  const insightDatas = Array.isArray(feed?.insightDatas) ? feed.insightDatas : [];
  return !!insightDatas.find((d: any) => String(d?.sceneId ?? '') === id);
}

function isChecked(value: any): boolean {
  return value === true;
}

function isReportFeedChecked(feed: any): boolean {
  return feed?.checked === true;
}

function getCollectionReportAssociatedFeedDisplayName(feed: any): string {
  return safeTrim(feed?.feedName) || safeTrim(feed?.name);
}

function getCollectionReportValueAddedProductLabel(item: any): string {
  const product = safeTrim(item?.product) || safeTrim(item?.name);
  const resolution = safeTrim(item?.resolution);

  if (product && resolution) return `${product} (${resolution})`;
  return product || resolution;
}

export function getCollectionReportSelectedValueAddedProductLabels(sensor: any): string[] {
  const valueAddedOption = Array.isArray(sensor?.valueAddedOption) ? sensor.valueAddedOption : [];
  const indicesDetails = Array.isArray(sensor?.indicesDetails) ? sensor.indicesDetails : [];
  const superResolutions = Array.isArray(sensor?.superResolutions) ? sensor.superResolutions : [];

  return Array.from(
    new Set(
      [...valueAddedOption, ...indicesDetails, ...superResolutions]
        .filter((item: any) => isChecked(item?.checked) || isChecked(item?.isChecked))
        .map((item: any) => getCollectionReportValueAddedProductLabel(item))
        .filter(Boolean),
    ),
  );
}

function sensorHasCollectionReportSelection(sensor: any): boolean {
  if (isChecked(sensor?.checked) || isChecked(sensor?.isChecked)) return true;

  const scenes = Array.isArray(sensor?.scenes) ? sensor.scenes : [];
  if (scenes.some((scene: any) => isChecked(scene?.checked) || isChecked(scene?.isChecked))) {
    return true;
  }

  const infoServices = Array.isArray(sensor?.InfoServices)
    ? sensor.InfoServices
    : Array.isArray(sensor?.informationServices)
      ? sensor.informationServices
      : [];
  const associatedFeeds = Array.isArray(sensor?.AssociatedFeeds)
    ? sensor.AssociatedFeeds
    : [];

  if (getCollectionReportSelectedValueAddedProductLabels(sensor).length) {
    return true;
  }

  return [
    ...infoServices,
    ...associatedFeeds,
  ].some((feed: any) => isReportFeedChecked(feed));
}

export function buildCollectionReportSelectedFeedsTree(args: {
  collection: any;
  currentUser: any;
  isFeedTasking: boolean;
  maxChars?: number;
}): CollectionReportFeedTreeV1 {
  const maxChars = typeof args.maxChars === 'number' ? args.maxChars : Infinity;
  const summary: CollectionReportFeedTreeV1 = { v: 1, s: [] };

  const sensors = Array.isArray(args.collection?.sensors)
    ? args.collection.sensors.filter((sensor: any) => sensorHasCollectionReportSelection(sensor))
    : [];

  for (const sensor of sensors) {
    const sensorName = getCollectionReportSensorDisplayName({
      sensor,
      currentUser: args.currentUser,
      isFeedTasking: args.isFeedTasking,
    });
    if (!sensorName) continue;
    const selectedProducts = getCollectionReportSelectedValueAddedProductLabels(sensor);

    const selectedScenes = Array.isArray(sensor?.scenes)
      ? sensor.scenes.filter((scene: any) => isChecked(scene?.checked) || isChecked(scene?.isChecked))
      : [];

    const infoServices = Array.isArray(sensor?.InfoServices)
      ? sensor.InfoServices
      : Array.isArray(sensor?.informationServices)
        ? sensor.informationServices
        : [];
    const checkedFeeds = infoServices.filter((f: any) => isReportFeedChecked(f));
    const associatedFeeds = Array.isArray(sensor?.AssociatedFeeds)
      ? sensor.AssociatedFeeds
      : [];
    const checkedAssociatedFeeds = associatedFeeds.filter((feed: any) => isReportFeedChecked(feed));

    const seenSceneKeys = new Set<string>();

    const nextSensor: CollectionReportFeedTreeV1['s'][number] = {
      n: sensorName,
      sc: [] as CollectionReportFeedTreeV1['s'][number]['sc'],
    };
    if (selectedProducts.length) nextSensor.p = selectedProducts;
    summary.s.push(nextSensor);

    if (safeJsonLength({ feedTree: summary }) > maxChars) {
      summary.s.pop();
      summary.t = 1;
      break;
    }

    for (const scene of selectedScenes) {
      const sceneIdx = scene?.sceneIdx != null ? String(scene.sceneIdx) : '';
      const scenePrefix = getCollectionReportSceneSelectionPrefix(scene);
      const sceneLabel = sceneIdx
        ? `${scenePrefix} ${sceneIdx}`
        : safeTrim(scene?.sceneName) || safeTrim(scene?.name) || safeTrim(scene?.id);
      const sceneId = scene?.id || scene?.scene_id;
      if (!sceneLabel) continue;

      const feedNames = args.isFeedTasking
        ? []
        : checkedFeeds
            .map((feed: any) => getCollectionReportFeedDisplayName(feed))
            .filter(Boolean)
            .filter((name: string, index: number, arr: string[]) => arr.indexOf(name) === index)
            .filter((name: string) => {
              const feed = checkedFeeds.find((candidate: any) => getCollectionReportFeedDisplayName(candidate) === name);
              return collectionReportFeedHasInsightForScene(feed, sceneId);
            });
      const associatedFeedNames = args.isFeedTasking
        ? []
        : checkedAssociatedFeeds
            .map((feed: any) => getCollectionReportAssociatedFeedDisplayName(feed))
            .filter(Boolean)
            .filter((name: string, index: number, arr: string[]) => arr.indexOf(name) === index);

      const sceneKey = JSON.stringify({
        name: sceneLabel,
        feeds: [...feedNames].sort(),
        associatedFeeds: [...associatedFeedNames].sort(),
      });
      if (seenSceneKeys.has(sceneKey)) continue;
      seenSceneKeys.add(sceneKey);

      nextSensor.sc.push({
        n: sceneLabel,
        f: feedNames,
        ...(associatedFeedNames.length ? { af: associatedFeedNames } : {}),
      });

      if (safeJsonLength({ feedTree: summary }) > maxChars) {
        nextSensor.sc.pop();
        summary.t = 1;
        break;
      }
    }

    if (summary.t) break;
  }

  return summary;
}

/**
 * Builds the selections payload sent to the backend for PDF rendering.
 *
 * The payload is frontend-authored and render-ready so the EJS template can
 * render it without replicating selection or formatting logic.
 */
export function buildCollectionReportSelectionsSummaryJson(args: {
  sections?: CollectionReportSelectionsSummaryV2['sections'];
}): string {
  const summary: CollectionReportSelectionsSummaryV2 = {
    v: 2,
  };

  if (Array.isArray(args.sections) && args.sections.length) {
    const sections = sanitizeCollectionReportSelectionSections(args.sections);
    if (sections.length) summary.sections = sections;
  }

  try {
    return JSON.stringify(summary);
  } catch {
    return '';
  }
}

export type CaptureElementAsJpegOptions = {
  /**
   * If true, elements with the class `aoi-capture-layer` are made visible in the CLONED DOM only.
   * This is useful to include AOI outlines in the exported image without showing them to the user.
   */
  revealAoiInClone?: boolean;

  /**
   * If true, the cloned DOM is modified to show ONLY the base map tiles + AOI outline.
   * This hides Leaflet controls and most overlay/marker panes in the clone.
   */
  aoiOnlyInClone?: boolean;

  /**
   * Override the maximum output dimension used while rasterizing the DOM.
   * Useful for fast previews that don't need the full report capture size.
   */
  maxDim?: number;

  /**
   * How long to wait for descendant <img> elements to settle before capture.
   * Lower values improve preview speed at the risk of missing late tiles.
   */
  imageSettleTimeoutMs?: number;
};

export async function captureElementAsJpegBlob(
  el: HTMLElement,
  options: CaptureElementAsJpegOptions = {},
): Promise<Blob> {
  // Give the browser a moment to paint recent layer/DOM updates (Leaflet panes, overlays).
  if (typeof requestAnimationFrame === 'function') {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }

  const canvas = await captureElementAsCanvas(el, options);
  try {
    return await canvasToCappedJpegBlob(canvas);
  } finally {
    releaseCanvas(canvas);
  }
}

async function captureLeafletCompareElementAsJpegBlobViaStitch(args: {
  el: HTMLElement;
  dividerX: number;
  options?: CaptureElementAsJpegOptions;
}): Promise<Blob> {
  const options = args.options || {};
  const el = args.el;
  const dividerVisualX = resolveLeafletCompareDividerVisualX(el, args.dividerX);

  const captureScale = resolveCollectionReportCaptureScale(
    el,
    typeof options.maxDim === 'number' && options.maxDim > 0
      ? options.maxDim
      : COLLECTION_REPORT_CAPTURE_MAX_DIM,
  );

  const hidePane = (scope: HTMLElement, paneClass: string) => {
    const pane = scope.querySelector<HTMLElement>(paneClass);
    if (!pane) return;
    pane.style.display = 'none';
    pane.style.visibility = 'hidden';
    pane.style.opacity = '0';
  };

  const leftCanvas = await captureElementAsCanvas(el, options, ({ scope }) => {
    hidePane(scope, '.leaflet-right-pane');
  });

  const rightCanvas = await captureElementAsCanvas(el, options, ({ scope }) => {
    hidePane(scope, '.leaflet-left-pane');
  });

  let out: HTMLCanvasElement | null = null;

  try {
    const width = leftCanvas.width;
    const height = leftCanvas.height;
    if (rightCanvas.width !== width || rightCanvas.height !== height) {
      // Fallback to single capture if sizing diverges unexpectedly.
      return await canvasToCappedJpegBlob(leftCanvas);
    }

    out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const ctx = out.getContext('2d');
    if (!ctx) {
      return await canvasToCappedJpegBlob(leftCanvas);
    }

    const splitX = Math.round(clamp(dividerVisualX * captureScale, 0, width));
    if (splitX <= 0 || splitX >= width) {
      ctx.drawImage(leftCanvas, 0, 0);
      return await canvasToCappedJpegBlob(out);
    }

    ctx.drawImage(leftCanvas, 0, 0, splitX, height, 0, 0, splitX, height);
    ctx.drawImage(rightCanvas, splitX, 0, width - splitX, height, splitX, 0, width - splitX, height);

    return await canvasToCappedJpegBlob(out);
  } finally {
    releaseCanvas(leftCanvas);
    releaseCanvas(rightCanvas);
    releaseCanvas(out);
  }
}

/**
 * Robust capture for Leaflet "compare" split.
 * Keep using the stitched left/right capture path because single-pass html2canvas
 * can flatten the compare panes and paint one scene over the other.
 */
export async function captureLeafletCompareElementAsJpegBlob(args: {
  el: HTMLElement;
  dividerX: number;
  options?: CaptureElementAsJpegOptions;
}): Promise<Blob> {
  const options = args.options || {};
  const el = args.el;

  // Allow Leaflet to settle.
  if (typeof requestAnimationFrame === 'function') {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }

  return await captureLeafletCompareElementAsJpegBlobViaStitch(args);
}

// Backwards-compatible helper (avoids refactoring all call sites at once).
export async function captureElementAsJpegDataUrl(el: HTMLElement): Promise<string> {
  const blob = await captureElementAsJpegBlob(el);
  return await blobToDataUrl(blob);
}
